/**
 * Cutting a picture into jigsaw pieces.
 *
 * The one thing that has to be true, and that is easy to get silently wrong:
 * where two pieces meet, one must have a tab exactly where the other has a
 * blank. Get a sign backwards and you produce two pieces that both bulge into
 * the same space — they still LOOK like jigsaw pieces, and the puzzle is
 * quietly impossible. So the edges are generated once, from the shared side,
 * and each piece reads its own from that.
 */

/** 1 is a tab sticking out, -1 a blank cut in, 0 a straight outside edge. */
export type EdgeSign = -1 | 0 | 1;

export interface Piece {
  row: number;
  col: number;
  top: EdgeSign;
  right: EdgeSign;
  bottom: EdgeSign;
  left: EdgeSign;
}

export interface Puzzle {
  rows: number;
  cols: number;
  pieces: Piece[];
}

function random(seed: number): () => number {
  let state = (seed | 0) || 1;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function makePuzzle(rows: number, cols: number, seed: number): Puzzle {
  const r = Math.max(1, Math.floor(rows));
  const c = Math.max(1, Math.floor(cols));
  const rand = random(seed);

  // Decide each shared edge ONCE, then hand both neighbours their view of it.
  const vertical: EdgeSign[][] = []; // between (row, col) and (row, col + 1)
  for (let row = 0; row < r; row++) {
    vertical.push(Array.from({ length: Math.max(0, c - 1) }, () => (rand() > 0.5 ? 1 : -1) as EdgeSign));
  }
  const horizontal: EdgeSign[][] = []; // between (row, col) and (row + 1, col)
  for (let row = 0; row < Math.max(0, r - 1); row++) {
    horizontal.push(Array.from({ length: c }, () => (rand() > 0.5 ? 1 : -1) as EdgeSign));
  }

  const pieces: Piece[] = [];
  for (let row = 0; row < r; row++) {
    for (let col = 0; col < c; col++) {
      pieces.push({
        row,
        col,
        // Outside edges are straight; inside ones mirror the neighbour.
        left: col === 0 ? 0 : ((-vertical[row][col - 1]) as EdgeSign),
        right: col === c - 1 ? 0 : vertical[row][col],
        top: row === 0 ? 0 : ((-horizontal[row - 1][col]) as EdgeSign),
        bottom: row === r - 1 ? 0 : horizontal[row][col],
      });
    }
  }

  return { rows: r, cols: c, pieces };
}

export function pieceAt(puzzle: Puzzle, row: number, col: number): Piece | undefined {
  return puzzle.pieces.find((p) => p.row === row && p.col === col);
}

/** Corner pieces have two straight edges — the ones a child starts with. */
export function straightEdges(piece: Piece): number {
  return [piece.top, piece.right, piece.bottom, piece.left].filter((e) => e === 0).length;
}

/**
 * Where a piece starts, in fractions of the board — so y greater than 1 is
 * BELOW the board, in the tray.
 *
 * Two staggered rows rather than one: a single row of nine pieces overlaps so
 * heavily that picking the one you want is the hard part, and that is not the
 * skill the puzzle is for.
 */
export function scatterSpot(index: number, count: number, seed: number): { x: number; y: number } {
  const rand = random(seed + index * 7919);
  const perRow = Math.max(1, Math.ceil(count / 2));
  const row = Math.floor(index / perRow);
  const inRow = index % perRow;
  const lane = perRow <= 1 ? 0.5 : inRow / (perRow - 1);
  return {
    x: 0.1 + lane * 0.8 + (rand() - 0.5) * 0.04,
    // Clear of the board, and staggered so the two rows do not sit on top of
    // one another.
    y: 1.12 + row * 0.3 + (rand() - 0.5) * 0.05,
  };
}

export function isSolved(placed: boolean[]): boolean {
  return placed.length > 0 && placed.every(Boolean);
}
